/**
 * WRMS Landing Page — Role-based entry point
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/common/Sidebar';
import Navbar from '../../components/common/Navbar';
import EmployeeWRMSDashboard from '../components/dashboards/EmployeeWRMSDashboard';
import ApproverDashboard from '../components/dashboards/ApproverDashboard';
import HRAdminPanel from '../components/dashboards/HRAdminPanel';
import '../../wrms/css/wrms.css';

const APPROVER_ROLES = ['dept_head', 'production_head', 'director'];
const ADMIN_ROLES = ['hr', 'admin'];
const MANAGER_ROLES = ['manager'];

const WRMSLandingPage = () => {
  const { user } = useAuth();
  const role = user?.role || 'employee';

  const isApproverOrAdmin =
    APPROVER_ROLES.includes(role) ||
    ADMIN_ROLES.includes(role) ||
    MANAGER_ROLES.includes(role);

  const [viewMode, setViewMode] = useState('personal');

  // Sync viewMode to 'admin' when the user role is loaded and is an admin/approver
  useEffect(() => {
    if (isApproverOrAdmin) {
      setViewMode('admin');
    } else {
      setViewMode('personal');
    }
  }, [role, isApproverOrAdmin]);

  const renderDashboard = () => {
    if (viewMode === 'personal') {
      return <EmployeeWRMSDashboard />;
    }
    if (ADMIN_ROLES.includes(role)) return <HRAdminPanel />;
    if (APPROVER_ROLES.includes(role) || MANAGER_ROLES.includes(role)) return <ApproverDashboard />;
    return <EmployeeWRMSDashboard />;
  };

  const getPageTitle = () => {
    if (viewMode === 'personal') return 'My Request Dashboard';
    if (ADMIN_ROLES.includes(role)) return 'Admin Control Panel';
    if (APPROVER_ROLES.includes(role) || MANAGER_ROLES.includes(role)) return 'Approvals Dashboard';
    return 'My Request Dashboard';
  };

  const getPageSubtitle = () => {
    if (viewMode === 'personal') return 'Submit, track, and manage your leave requests.';
    if (ADMIN_ROLES.includes(role)) return 'Full visibility and control over all leave requests across the organization.';
    if (APPROVER_ROLES.includes(role) || MANAGER_ROLES.includes(role)) return 'Review and action pending leave requests assigned to your approval stage.';
    return 'Submit, track, and manage your leave requests.';
  };

  return (
    <div className="d-flex wrms-page" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 wrms-main-content" style={{ minWidth: 0, maxWidth: '100vw' }}>
        <Navbar />
        <div className="wrms-container">
          <div className="wrms-page-header">
            <div className="wrms-header-info">
              <div className="wrms-breadcrumb">HR Control · <span>Leave Management</span></div>
              <h1 className="wrms-page-title">{getPageTitle()}</h1>
              <p className="wrms-page-subtitle">{getPageSubtitle()}</p>
            </div>

            {isApproverOrAdmin && (
              <div className="wrms-report-tab wrms-landing-tabs">
                <button
                  className={`wrms-report-tab-btn ${viewMode === 'personal' ? 'active' : ''}`}
                  onClick={() => setViewMode('personal')}
                >
                  My Requests
                </button>
                <button
                  className={`wrms-report-tab-btn ${viewMode === 'admin' ? 'active' : ''}`}
                  onClick={() => setViewMode('admin')}
                >
                  {ADMIN_ROLES.includes(role) ? 'HR/Admin Control' : 'Approvals Panel'}
                </button>
              </div>
            )}
          </div>

          {renderDashboard()}
        </div>
      </div>
    </div>
  );
};

export default WRMSLandingPage;
